import React, { useEffect, useRef } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import { IntlProvider } from 'react-intl';
import { Spinner } from 'reactstrap';
import NotificationAlert from 'react-notification-alert';
import numeral from 'numeral';
import moment from 'moment';
import 'moment-timezone';
import 'moment/locale/es';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import 'react-notification-alert/dist/animate.css';
import 'react-perfect-scrollbar/dist/css/styles.css';
import 'assets/vendor/fullcalendar/dist/fullcalendar.min.css';
import 'assets/vendor/sweetalert2/dist/sweetalert2.min.css';
import 'assets/vendor/select2/dist/css/select2.min.css';
import 'assets/vendor/quill/dist/quill.core.css';
import 'assets/vendor/nucleo/css/nucleo.css';
import 'assets/vendor/@fortawesome/fontawesome-free/css/all.min.css';
import 'assets/scss/argon-dashboard-pro-react.scss?v1.0.0';
import 'assets/scss/new.css';

import 'config/Numeral';
import PrivateLayout from 'layouts/Private';
import AuthLayout from 'layouts/Auth';
import AppLocale from 'i18n';
import { Intl, ErrorBoundary } from 'components';

import { useLocation } from "react-router";
import SettingsActions from "store/reducers/Settings";

const stripePromise = loadStripe( process.env.REACT_APP_STRIPE_KEY );

function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const PrivateRoute = ( { component: Component, ...rest } ) => (
  <Route
    {...rest}
    render={( props ) => (
      rest.user && rest.user.access_token
        ? <Component {...props} />
        : <AuthLayout {...props} />
    )}
  />
);

const PublicRoute = ( { component: Component, ...rest } ) => (
  <Route
    {...rest}
    render={( props ) => (
      !rest.user || !rest.user.access_token
        ? <Component {...props} />
        : <Redirect to={{ pathname: '/out/new-campaign', state: { from: props.location } }} />
    )}
  />
);

const App = ( {
  user, locale, isLoading, alert, switchLanguage,
} ) => {
  numeral.locale( locale.locale );
  moment.locale( locale.locale );
  numeral.localeData( 'es' ).currency.symbol = user.currency_symbol;
  numeral.localeData( 'en' ).currency.symbol = user.currency_symbol;

  const messages = AppLocale[locale.locale];
  const notificationAlert = useRef( null );
  const isInitialMount = useRef( true );

  let query = useQuery();

  const Language = (lang) => {
    if (lang) {
      locale = lang.toLowerCase();
    } else {
      locale = "en";
    }
    switchLanguage({
      locale: locale == "en" ? "en" : "es",
      name: locale == "en" ? "english" : "spanish",
    });
  };

  useEffect(() => {
    var params = query.get('language')
      console.log("hello",params)
      try {
        if (params) {
          Language(params);
        }
      } catch (error) {}
    }, []);

  useEffect( () => {
    if ( isInitialMount.current ) {
      isInitialMount.current = false;
    } else if ( alert && alert.msg ) {
      const options = {
        place: 'tr',
        message: (
          <div className="alert-text">
            <span data-notify="message">
              <Intl id={alert.msg} />
            </span>
          </div>
        ),
        type: alert.alertType || 'danger',
        icon: 'ni ni-bell-55',
        autoDismiss: 10,
      };
      if ( alert ) notificationAlert.current.notificationAlert( options );
    }
  }, [alert] );

  return (
    <ErrorBoundary>
      <IntlProvider
        locale={locale.locale}
        messages={messages}
      >
        <Elements stripe={stripePromise} options={{ locale: locale.locale }}>
          <div className="rna-wrapper">
            <NotificationAlert ref={notificationAlert} />
          </div>

          <Switch>
            <PublicRoute path="/auth" component={AuthLayout} user={user} />
            <PrivateRoute path="/" component={PrivateLayout} user={user} />
            <Redirect from="*" to="/out/new-campaign" />
          </Switch>

          {isLoading
          && (
            <div className="app-loading">
              <Spinner type="grow" color="primary" style={{ width: '3rem', height: '3rem' }} />
            </div>
          )}
        </Elements>
      </IntlProvider>
    </ErrorBoundary>
  );
};

const mapStateToProps = ( { settings, user } ) => {
  const { locale, isLoading, alert } = settings;
  return {
    locale, isLoading, alert, user,
  };
};

const mapDispatchToProps = {
  toggleLoading: SettingsActions.toggleLoading,
  toggleErrorAlert: SettingsActions.toggleErrorAlert,
  switchLanguage: SettingsActions.switchLanguage,
};

export default connect( mapStateToProps, mapDispatchToProps )( App );
